/**
 * Macmerise Variant Images
 * Filters product gallery to show only images for selected color variant
 *
 * LOGIC: Uses variant featured_media as boundary markers.
 * If Navy Blue's featured image is at position 5 and Purple's is at position 12:
 *   - Navy Blue shows images 5-11
 *   - Purple shows images 12 to end (or next variant's position)
 *
 * This approach works because each variant's featured image marks the START
 * of that color's image set.
 */

(function() {
  'use strict';

  class MacmeriseVariantImages {
    constructor() {
      this.productData = null;
      this.colorOptionIndex = -1;
      this.colorValues = [];
      this.colorImageRanges = {}; // Maps color -> {startIndex, endIndex}
      this.initialized = false;
      this.currentColor = null;

      // Wait for DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      // Only run on product pages
      if (!window.location.pathname.includes('/products/')) {
        return;
      }

      // Load product data
      this.loadProductData();

      if (!this.productData) {
        console.log('[VariantImages] No product data found');
        return;
      }

      // Find color option
      this.findColorOption();

      if (this.colorOptionIndex === -1) {
        console.log('[VariantImages] No color option found');
        return;
      }

      // Calculate image ranges for each color based on variant featured_media
      this.calculateImageRanges();

      console.log('[VariantImages] Found color option at position', this.colorOptionIndex);
      console.log('[VariantImages] Color values:', this.colorValues);
      console.log('[VariantImages] Image ranges:', this.colorImageRanges);
      console.log('[VariantImages] Total media items:', this.productData.media?.length || 0);

      // Setup event listeners
      this.setupEventListeners();

      // Apply initial filtering
      setTimeout(() => this.applyInitialFilter(), 150);

      this.initialized = true;
      console.log('[VariantImages] Initialized successfully');
    }

    loadProductData() {
      const dataEl = document.getElementById('macmerise-product-data');
      if (dataEl) {
        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('[VariantImages] Failed to parse product data:', e);
        }
      }
    }

    findColorOption() {
      if (!this.productData || !this.productData.options) return;

      const colorLabels = ['color', 'colour'];

      this.productData.options.forEach((optionName, index) => {
        const normalizedName = optionName.toLowerCase().trim();
        if (colorLabels.some(label => normalizedName.includes(label))) {
          this.colorOptionIndex = index + 1; // Shopify uses 1-based (option1, option2, option3)
        }
      });

      if (this.colorOptionIndex === -1) return;

      // Get unique color values in order from variants
      const seen = new Set();
      this.productData.variants.forEach(variant => {
        const colorValue = variant[`option${this.colorOptionIndex}`];
        if (colorValue && !seen.has(colorValue.toLowerCase())) {
          seen.add(colorValue.toLowerCase());
          this.colorValues.push(colorValue);
        }
      });
    }

    calculateImageRanges() {
      // Build a map of color -> featured_media position
      const colorPositions = [];
      const media = this.productData.media || [];

      this.colorValues.forEach(color => {
        // Find the first variant with this color that has a featured_media
        const variant = this.productData.variants.find(v => {
          const variantColor = v[`option${this.colorOptionIndex}`];
          return variantColor && variantColor.toLowerCase() === color.toLowerCase() && v.featured_media;
        });

        if (variant && variant.featured_media) {
          // Find the position of this media in the media array
          const mediaIndex = media.findIndex(m => m.id === variant.featured_media.id);
          if (mediaIndex !== -1) {
            colorPositions.push({
              color: color.toLowerCase(),
              position: mediaIndex,
              mediaId: variant.featured_media.id
            });
          }
        }
      });

      // Sort by position
      colorPositions.sort((a, b) => a.position - b.position);

      console.log('[VariantImages] Color positions from featured_media:', colorPositions);

      // If we have positions, calculate ranges
      if (colorPositions.length > 0) {
        colorPositions.forEach((cp, index) => {
          const startIndex = cp.position;
          const endIndex = (index < colorPositions.length - 1)
            ? colorPositions[index + 1].position
            : media.length;

          this.colorImageRanges[cp.color] = {
            startIndex,
            endIndex,
            mediaIds: media.slice(startIndex, endIndex).map(m => m.id)
          };
        });

        // Handle colors that might not have featured_media - assign them remaining images
        this.colorValues.forEach(color => {
          const normalizedColor = color.toLowerCase();
          if (!this.colorImageRanges[normalizedColor]) {
            // Fallback: this color has no featured_media, use even division for it
            console.log(`[VariantImages] Color "${color}" has no featured_media, will use fallback`);
          }
        });
      } else {
        // Fallback: no featured_media found, use even division
        console.log('[VariantImages] No featured_media found, using even division fallback');
        this.calculateEvenDivision();
      }
    }

    calculateEvenDivision() {
      const media = this.productData.media || [];
      const imagesPerColor = Math.ceil(media.length / this.colorValues.length);

      this.colorValues.forEach((color, index) => {
        const startIndex = index * imagesPerColor;
        const endIndex = Math.min(startIndex + imagesPerColor, media.length);

        this.colorImageRanges[color.toLowerCase()] = {
          startIndex,
          endIndex,
          mediaIds: media.slice(startIndex, endIndex).map(m => m.id)
        };
      });
    }

    setupEventListeners() {
      // Method 1: Listen for Impact theme's variant change event on the form
      const form = document.querySelector('form[data-product-form]');
      if (form) {
        form.addEventListener('variant:change', (event) => {
          console.log('[VariantImages] variant:change event fired', event.detail);
          const variant = event.detail?.variant;
          if (variant) {
            const colorValue = variant[`option${this.colorOptionIndex}`];
            if (colorValue) {
              console.log('[VariantImages] Variant changed to color:', colorValue);
              this.filterGallery(colorValue);
            }
          }
        });
      }

      // Method 2: Listen for custom event from macmerise-swatches (works for out-of-stock)
      document.addEventListener('macmerise:colorchange', (event) => {
        const color = event.detail?.color;
        console.log('[VariantImages] macmerise:colorchange event received:', event.detail);
        if (color) {
          console.log('[VariantImages] Filtering for color from custom event:', color);
          this.currentColor = null; // Force update even if same color
          setTimeout(() => this.filterGallery(color), 50); // Small delay to ensure DOM is ready
        }
      });

      // Method 2b: Also listen on window (in case document events don't bubble)
      window.addEventListener('macmerise:colorchange', (event) => {
        const color = event.detail?.color;
        if (color && color !== this.currentColor) {
          console.log('[VariantImages] Window colorchange event:', color);
          this.currentColor = null;
          setTimeout(() => this.filterGallery(color), 50);
        }
      });

      // Method 3: Listen for changes on variant picker inputs directly
      this.setupDirectInputListeners();

      // Method 4: Listen for URL changes (for when variant changes via URL)
      this.setupURLChangeListener();

      // Method 5: Observe variant picker for attribute changes
      this.setupVariantPickerObserver();
    }

    setupDirectInputListeners() {
      const colorLabels = ['color', 'colour'];

      // Find all fieldsets and attach listeners
      const attachListeners = () => {
        const variantPickers = document.querySelectorAll('variant-picker');

        variantPickers.forEach(picker => {
          const fieldsets = picker.querySelectorAll('fieldset');

          fieldsets.forEach(fieldset => {
            const legend = fieldset.querySelector('legend');
            if (!legend) return;

            const legendText = legend.textContent.replace(':', '').trim().toLowerCase();

            if (colorLabels.some(label => legendText.includes(label))) {
              // Found color fieldset - attach listeners to all inputs
              const inputs = fieldset.querySelectorAll('input[type="radio"]');

              inputs.forEach(input => {
                // Remove existing listener to avoid duplicates
                input.removeEventListener('change', this.handleInputChange);

                // Add new listener
                input.addEventListener('change', this.handleInputChange.bind(this));

                // Also listen on click for custom swatches
                input.removeEventListener('click', this.handleInputClick);
                input.addEventListener('click', this.handleInputClick.bind(this));
              });

              // Also attach to custom swatch elements
              const swatches = fieldset.querySelectorAll('.mm-swatch-circular, .mm-swatch-pill, .mm-swatch-polaroid');
              swatches.forEach(swatch => {
                swatch.addEventListener('click', () => {
                  setTimeout(() => {
                    const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
                    if (checkedInput) {
                      const color = this.getColorValueFromInput(checkedInput);
                      if (color) {
                        console.log('[VariantImages] Swatch clicked, color:', color);
                        this.filterGallery(color);
                      }
                    }
                  }, 100);
                });
              });
            }
          });
        });
      };

      // Attach immediately
      attachListeners();

      // Re-attach after a delay (for dynamically rendered content)
      setTimeout(attachListeners, 500);
      setTimeout(attachListeners, 1000);
    }

    handleInputChange(event) {
      const input = event.target;
      if (input.checked) {
        const color = this.getColorValueFromInput(input);
        if (color) {
          console.log('[VariantImages] Input changed, color:', color);
          this.filterGallery(color);
        }
      }
    }

    handleInputClick(event) {
      const input = event.target;
      setTimeout(() => {
        if (input.checked) {
          const color = this.getColorValueFromInput(input);
          if (color) {
            console.log('[VariantImages] Input clicked, color:', color);
            this.filterGallery(color);
          }
        }
      }, 50);
    }

    setupURLChangeListener() {
      // Listen for popstate (back/forward navigation)
      window.addEventListener('popstate', () => {
        setTimeout(() => this.applyInitialFilter(), 100);
      });

      // Observe URL changes via History API
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      const self = this;

      history.pushState = function() {
        originalPushState.apply(this, arguments);
        setTimeout(() => self.applyInitialFilter(), 100);
      };

      history.replaceState = function() {
        originalReplaceState.apply(this, arguments);
        setTimeout(() => self.applyInitialFilter(), 100);
      };
    }

    setupVariantPickerObserver() {
      // Observe the variant picker for changes
      const variantPicker = document.querySelector('variant-picker');
      if (!variantPicker) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' || mutation.type === 'childList') {
            setTimeout(() => {
              const color = this.getCurrentSelectedColor();
              if (color && color !== this.currentColor) {
                console.log('[VariantImages] Observer detected color change:', color);
                this.filterGallery(color);
              }
            }, 50);
          }
        });
      });

      observer.observe(variantPicker, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['aria-checked', 'checked', 'aria-current']
      });
    }

    getCurrentSelectedColor() {
      const colorLabels = ['color', 'colour'];
      const variantPickers = document.querySelectorAll('variant-picker');

      for (const picker of variantPickers) {
        const fieldsets = picker.querySelectorAll('fieldset');

        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');
          if (!legend) continue;

          const legendText = legend.textContent.replace(':', '').trim().toLowerCase();

          if (colorLabels.some(label => legendText.includes(label))) {
            const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
              return this.getColorValueFromInput(checkedInput);
            }
          }
        }
      }

      return null;
    }

    getColorValueFromInput(input) {
      // Try multiple methods to get the color value

      // 1. Data attribute from macmerise swatches
      if (input.dataset.optionValue) {
        return input.dataset.optionValue;
      }

      // 2. Title from custom swatch (our circular swatches store it here)
      const parentContainer = input.closest('.variant-picker__option-values');
      if (parentContainer) {
        const swatch = parentContainer.querySelector(`.mm-swatch-circular[title]`);
        // Find the swatch that's right before or associated with this input
        const allInputs = parentContainer.querySelectorAll('input[type="radio"]');
        const inputIndex = Array.from(allInputs).indexOf(input);
        const swatches = parentContainer.querySelectorAll('.mm-swatch-circular[title]');
        if (swatches[inputIndex] && swatches[inputIndex].title) {
          return swatches[inputIndex].title;
        }
      }

      // 3. Check the label
      const label = input.nextElementSibling;
      if (label) {
        // Screen reader only text
        const srOnly = label.querySelector('.sr-only');
        if (srOnly && srOnly.textContent.trim()) {
          return srOnly.textContent.trim();
        }

        // Regular text content (but not if it's just whitespace)
        const text = label.textContent.trim();
        if (text && text.length > 0 && text.length < 50) {
          return text;
        }
      }

      // 4. Check aria-label
      if (input.getAttribute('aria-label')) {
        return input.getAttribute('aria-label');
      }

      // 5. Match input value against known colors
      const inputValue = input.value;
      const matchingColor = this.colorValues.find(c =>
        c.toLowerCase() === inputValue.toLowerCase()
      );
      if (matchingColor) return matchingColor;

      return null;
    }

    applyInitialFilter() {
      let colorToUse = null;

      // 1. First check URL for specific variant
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId && this.productData.variants) {
        const variant = this.productData.variants.find(v => v.id.toString() === variantId);
        if (variant) {
          colorToUse = variant[`option${this.colorOptionIndex}`];
          console.log('[VariantImages] Color from URL variant:', colorToUse);
        }
      }

      // 2. If no URL variant, try to get currently selected color from UI
      if (!colorToUse) {
        colorToUse = this.getCurrentSelectedColor();
        if (colorToUse) {
          console.log('[VariantImages] Color from UI selection:', colorToUse);
        }
      }

      // 3. If still no color, find first IN-STOCK color
      if (!colorToUse) {
        colorToUse = this.getFirstAvailableColor();
        if (colorToUse) {
          console.log('[VariantImages] Using first available color:', colorToUse);
        }
      }

      // 4. If all colors are out of stock, use first color
      if (!colorToUse && this.colorValues.length > 0) {
        colorToUse = this.colorValues[0];
        console.log('[VariantImages] All colors out of stock, using first color:', colorToUse);
      }

      if (colorToUse) {
        console.log('[VariantImages] Initial filter for color:', colorToUse);
        this.filterGallery(colorToUse);

        // Also update the UI to show this color as selected
        this.selectColorInUI(colorToUse);
      }
    }

    getFirstAvailableColor() {
      // Find the first color that has at least one available variant
      for (const color of this.colorValues) {
        const hasAvailable = this.productData.variants.some(variant => {
          const variantColor = variant[`option${this.colorOptionIndex}`];
          return variantColor &&
                 variantColor.toLowerCase() === color.toLowerCase() &&
                 variant.available;
        });

        if (hasAvailable) {
          return color;
        }
      }

      return null; // All colors are out of stock
    }

    selectColorInUI(colorToSelect) {
      // Find and click the swatch for this color to sync UI state
      const normalizedColor = colorToSelect.toLowerCase().trim();
      const colorLabels = ['color', 'colour'];

      const variantPickers = document.querySelectorAll('variant-picker');

      variantPickers.forEach(picker => {
        const fieldsets = picker.querySelectorAll('fieldset');

        fieldsets.forEach(fieldset => {
          const legend = fieldset.querySelector('legend');
          if (!legend) return;

          const legendText = legend.textContent.replace(':', '').trim().toLowerCase();

          if (colorLabels.some(label => legendText.includes(label))) {
            // Found color fieldset - find the matching input
            const inputs = fieldset.querySelectorAll('input[type="radio"]');

            inputs.forEach(input => {
              const inputColor = this.getColorValueFromInput(input);
              if (inputColor && inputColor.toLowerCase().trim() === normalizedColor) {
                // Found matching input - select it
                if (!input.checked) {
                  const wasDisabled = input.disabled;
                  if (wasDisabled) input.disabled = false;

                  input.checked = true;
                  input.dispatchEvent(new Event('change', { bubbles: true }));

                  if (wasDisabled) input.disabled = true;

                  // Update visual state of custom swatches
                  const swatches = fieldset.querySelectorAll('.mm-swatch-circular');
                  swatches.forEach(s => s.classList.remove('mm-selected'));

                  const allInputs = fieldset.querySelectorAll('input[type="radio"]');
                  const inputIndex = Array.from(allInputs).indexOf(input);
                  if (swatches[inputIndex]) {
                    swatches[inputIndex].classList.add('mm-selected');
                  }
                }
              }
            });
          }
        });
      });
    }

    filterGallery(selectedColor) {
      if (!selectedColor || !this.productData?.media) {
        console.log('[VariantImages] Cannot filter - missing data');
        return;
      }

      const normalizedColor = selectedColor.toLowerCase().trim();

      // Skip if same color
      if (this.currentColor === normalizedColor) {
        console.log('[VariantImages] Same color, skipping filter');
        return;
      }

      this.currentColor = normalizedColor;
      const media = this.productData.media;

      console.log('[VariantImages] Filtering for:', normalizedColor);

      // Get media IDs to show from our pre-calculated ranges
      let mediaIdsToShow;

      if (this.colorImageRanges[normalizedColor]) {
        mediaIdsToShow = new Set(this.colorImageRanges[normalizedColor].mediaIds);
        console.log('[VariantImages] Using calculated range:', this.colorImageRanges[normalizedColor]);
      } else {
        // Fallback: show all
        console.log('[VariantImages] No range found for color, showing all');
        mediaIdsToShow = new Set(media.map(m => m.id));
      }

      console.log('[VariantImages] Media IDs to show:', Array.from(mediaIdsToShow));

      // Apply to gallery
      this.applyFilter(mediaIdsToShow);
    }

    applyFilter(mediaIdsToShow) {
      const gallery = document.querySelector('product-gallery, .product-gallery');
      if (!gallery) {
        console.log('[VariantImages] No gallery element found');
        return;
      }

      // Get all media items
      const mediaItems = gallery.querySelectorAll('.product-gallery__media[data-media-id]');
      const thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail');
      const pageDots = gallery.querySelectorAll('page-dots button');

      console.log('[VariantImages] Found', mediaItems.length, 'media items in DOM');

      let firstVisibleItem = null;
      let firstVisibleIndex = -1;
      let shownCount = 0;
      let hiddenCount = 0;

      // Process main media items
      mediaItems.forEach((item, index) => {
        const mediaId = parseInt(item.dataset.mediaId);
        const shouldShow = mediaIdsToShow.has(mediaId);

        if (shouldShow) {
          // Show this item
          item.removeAttribute('hidden');
          item.style.display = '';
          item.style.visibility = '';
          item.style.position = '';
          item.style.width = '';
          item.style.height = '';
          item.classList.remove('mm-variant-hidden');

          if (firstVisibleItem === null) {
            firstVisibleItem = item;
            firstVisibleIndex = index;
          }
          shownCount++;
        } else {
          // Hide this item
          item.setAttribute('hidden', '');
          item.classList.add('mm-variant-hidden');
          hiddenCount++;
        }
      });

      console.log('[VariantImages] Shown:', shownCount, 'Hidden:', hiddenCount);

      // Process thumbnails - match by index to media items
      thumbnails.forEach((thumb, index) => {
        if (index < mediaItems.length) {
          const mediaItem = mediaItems[index];
          const isHidden = mediaItem.hasAttribute('hidden');

          if (isHidden) {
            thumb.setAttribute('hidden', '');
            thumb.classList.add('mm-variant-hidden');
          } else {
            thumb.removeAttribute('hidden');
            thumb.classList.remove('mm-variant-hidden');
          }
        }
      });

      // Process page dots
      pageDots.forEach((dot, index) => {
        if (index < mediaItems.length) {
          const mediaItem = mediaItems[index];
          const isHidden = mediaItem.hasAttribute('hidden');

          if (isHidden) {
            dot.setAttribute('hidden', '');
          } else {
            dot.removeAttribute('hidden');
          }
        }
      });

      // Navigate to first visible image
      if (firstVisibleItem) {
        this.scrollToFirstVisible(gallery, firstVisibleItem);
      }

      // Trigger layout updates
      this.triggerLayoutUpdate(gallery);
    }

    scrollToFirstVisible(gallery, firstVisibleItem) {
      const mediaCarousel = gallery.querySelector('media-carousel');

      if (mediaCarousel) {
        // Find the index among visible items
        const allMedia = gallery.querySelectorAll('.product-gallery__media[data-media-id]');
        const visibleMedia = Array.from(allMedia).filter(m => !m.hasAttribute('hidden'));
        const visibleIndex = visibleMedia.indexOf(firstVisibleItem);

        // Try various carousel navigation methods
        if (typeof mediaCarousel.scrollToCell === 'function') {
          mediaCarousel.scrollToCell(0, false);
        } else if (typeof mediaCarousel.goTo === 'function') {
          mediaCarousel.goTo(0);
        } else if (typeof mediaCarousel.select === 'function') {
          mediaCarousel.select(0);
        }

        // Scroll into view as backup
        requestAnimationFrame(() => {
          firstVisibleItem.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'start'
          });
        });
      }

      // Update page dots selection
      const pageDots = gallery.querySelector('page-dots');
      if (pageDots) {
        const buttons = pageDots.querySelectorAll('button:not([hidden])');
        buttons.forEach((btn, i) => {
          btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        });
      }

      // Update thumbnail selection
      const thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail:not([hidden])');
      thumbnails.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      });
    }

    triggerLayoutUpdate(gallery) {
      // Dispatch resize event to trigger layout recalculation
      window.dispatchEvent(new Event('resize'));

      // Dispatch custom events
      gallery.dispatchEvent(new CustomEvent('variant:imagefilter', { bubbles: true }));
      gallery.dispatchEvent(new CustomEvent('product:mediaChange', { bubbles: true }));

      // Force carousel update
      const mediaCarousel = gallery.querySelector('media-carousel');
      if (mediaCarousel) {
        mediaCarousel.dispatchEvent(new Event('resize'));

        // Some carousels need a refresh
        if (typeof mediaCarousel.refresh === 'function') {
          mediaCarousel.refresh();
        }
        if (typeof mediaCarousel.reposition === 'function') {
          mediaCarousel.reposition();
        }
      }
    }
  }

  // Add styles for hiding
  const style = document.createElement('style');
  style.textContent = `
    .mm-variant-hidden {
      display: none !important;
      visibility: hidden !important;
      position: absolute !important;
      width: 0 !important;
      height: 0 !important;
      overflow: hidden !important;
      pointer-events: none !important;
      opacity: 0 !important;
    }

    /* Ensure hidden items don't affect layout */
    .product-gallery__media[hidden],
    .product-gallery__thumbnail[hidden],
    page-dots button[hidden] {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  // Initialize
  window.MacmeriseVariantImages = new MacmeriseVariantImages();

})();
